/**
 * @fileoverview InterimSuccess component for rendering a success state with navigation buttons
 *
 * @example
 * // Usage in component:
 * import { InterimSuccess } from './InterimSuccess'
 *
 * <InterimSuccess onSubmit={onSubmit} question={question} />
 */

import { Instructions } from './sections/Instructions'

import config from '../../config'
import { NavigationButton } from '../NavigationButton'
import { DesktopProgress, MobileProgress } from '../Progress/Progress'
import { Text } from '../typography/Typography'
import css from './InterimSuccess.module.css'
import { List } from './sections/List'
import { Title } from './sections/Title'

/**
 * @function InterimSuccess
 * @description Component for rendering a success state with navigation buttons
 * @param {Novoic.CheckboxQuestionProps} props - The props for the component
 * @returns {JSX.Element} The InterimSuccess component
 */
export function InterimSuccess({ onSubmit, question }: Novoic.CheckboxQuestionProps) {
    const { id, question: body } = question
    const { title, accent, instructions, list, subtext, checkboxes } = body

    const state = checkboxes.map((checkbox) => ({
        ...checkbox,
        value: checkbox.default,
        pristine: true,
    }))

    function onConfirm() {
        return onSubmit(id, {
            checkboxes: state.reduce((acc, checkbox) => {
                acc[checkbox.name] = checkbox.value
                return acc
            }, {} as Record<string, boolean>),
        })
    }

    return (
        <div className={css.container}>
            <MobileProgress />
            <div className={css.title_container}>
                <img
                    alt="success icon"
                    className={css.icon}
                    src={config.s3.getStaticURL('success.svg')}
                />
                <Title accent={accent}>{title}</Title>
            </div>
            {subtext && <Text>{subtext}</Text>}
            {instructions && <Instructions instructions={instructions} />}
            {list && <List list={list} />}

            <div className={css.button_container}>
                <DesktopProgress />
                <NavigationButton type="confirm" label={body.button} onClick={onConfirm} />
            </div>
        </div>
    )
}
